import { ReactNode, useEffect, useState } from "react";
import { Grid, IconButton, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { differenceInYears, format, parseISO } from "date-fns";
import { useManageModal, useSlideInContext } from "ui";
import SendIcon from '@mui/icons-material/Send';
import { isNullOrEmpty, dateOnlyISO8601, SCREEN_VERSION, Student, StudentDataType, Subscription } from "ft-shared-graphql";
import { SendToWhichEmail, SelectScreenProps, SelectScreenVersion } from "./components";
import { SCREEN_SUBSCRIPTION_OPTION, REQ_EMAIL_OPTIONS, LaunchProps, LaunchRequest } from "./LaunchType";
import { ArrowBack } from "@mui/icons-material";
type SEND_SESSION_REQUEST_ACTION = "local" | "redirect" | "notification" | "link";
function LaunchButton({ action: type, currentAction: action, onAction, children }: {
    action: SEND_SESSION_REQUEST_ACTION,
    currentAction: SEND_SESSION_REQUEST_ACTION | null,
    onAction: (action: SEND_SESSION_REQUEST_ACTION) => {}, children: ReactNode
}) {
    return (
        <LoadingButton
            sx={{
                backgroundColor: "primary.main",
                "&:hover": {
                    backgroundColor: "primary.light",
                },
                width: 180,
                marginTop: "16px",
                padding: 1
            }}
            type="submit"
            variant="contained"
            loading={type === action}
            loadingPosition="end"
            disabled={Boolean(action)}
            endIcon={<SendIcon />}
            onClick={() => onAction(type)}
        >
            {children}
        </LoadingButton>
    )
}


export default function LaunchForSelf({ studentData, updateStudent, subscriptions, onSubmit }: LaunchProps) {
    const { openModal } = useManageModal();
    const { disableClosingDrawer, toggleDrawer } = useSlideInContext();
    const [launchAction, setLaunchAction] = useState<SEND_SESSION_REQUEST_ACTION | null>(null);
    const [screenType, setScreenType] = useState<SCREEN_SUBSCRIPTION_OPTION | null>(null);
    const [reqEmail, setReqEmail] = useState<REQ_EMAIL_OPTIONS | null>(null)
    const [remoteLoadStatus, setRemoteLoadStatus] = useState<boolean>(false)

    const handleLaunch = async (action: SEND_SESSION_REQUEST_ACTION) => {

        // Prevent accidental closing of the application drawer
        disableClosingDrawer(true);

        if (action === "notification" && isNullOrEmpty(studentData?.parentEmail ?? "") && isNullOrEmpty(studentData?.email ?? "")) {
            handleNoEmailFound();
            return;
        }

        setLaunchAction(action);

        // Sleep for a simulated 10 seconds if intent is to send an email
        if (action === "notification") {
            openModal({ type: "REMOTE_LAUNCH_INITIALIZED", eventType: 'DISABLE_BACKDROP_AND_CTA', cancelButtonLabel: "Close" });
            // await sleep(1000 * 10)
        }
        const screenTypeParsed = screenType?.startsWith("TNS") ? "TeenScreen" : screenType?.startsWith("TS") ? "tScreen" : "TweenScreen";
        const languageParsed = screenType?.endsWith("ES") ? "Spanish" : "English";
        await onSubmit({
            action,
            screenTypeParsed,
            languageParsed,
            studentId: studentData!.id,
            email: reqEmail,
            launchPerson: null
        })
        // const { pid, lid } = decodedToken(accessToken);
        // const resposne = await fetch(`${window.__env.API_URL}/api/session/send`, {
        //     method: "post",
        //     headers: {
        //         Pid: pid,
        //         Lid: locationId ?? lid,
        //         "Content-Type": "application/json",
        //         authorization: `Bearer ${accessToken}`,
        //     },
        //     body: JSON.stringify({
        //         action: action,
        //         type: screenTypeParsed,
        //         language: languageParsed,
        //         studentId: studentData.id,
        //         email: reqEmail,
        //         enteredAt: format(new Date(), "yyyy-MM-dd'T'HH:mm:ssXXX")
        //     }),
        // });
        // if (resposne.status >= 400) {
        //     openModal({ type: "REMOTE_LAUNCH_FAILURE", cancelButtonLabel: "close" })
        // }

        // if (resposne.status === 202) {
        //     handleSuccessMessage();
        //     return;
        // }

        // if (resposne.status === 200) {
        //     const json = await resposne.json();
        //     if (action == "link") {
        //         openModal({ type: "LINK_PREPARED", cancelButtonLabel: "close", sessionLink: json.redirectUrl, modalAction: () => handleCopyLink(json.redirectUrl) })
        //         console.log("here is hte link: ", json.redirectUrl);
        //     } else {
        //         window.location.replace(json.redirectUrl);
        //         window.sessionStorage.clear();
        //     }
        // }

        setRemoteLoadStatus(false);
        disableClosingDrawer(false);
        setLaunchAction(null);
    };
    const handleCopyLink = async (link: string): Promise<void> => {
        try {
            await navigator.clipboard.writeText(link)
        } catch (err) {
            console.log("err", err)
        }
    }
    const handleNoEmailFound = () => {
        toggleDrawer();
        disableClosingDrawer(false);
        openModal({ type: "NO_EMAIL_FOUND", cancelButtonLabel: "Close" });
    };
    const handleSelectScreen = async (screen: SCREEN_SUBSCRIPTION_OPTION) => {
        setScreenType(screen)
    }
    const handleEmailSelect = async (email: REQ_EMAIL_OPTIONS) => {
        await setReqEmail(email)
        setRemoteLoadStatus(true)
        // await handleLaunch("notification")
    }
    useEffect(() => {
        if (reqEmail) {
            handleLaunch("notification")
        }
    }, [reqEmail])
    const handleSuccessMessage = () => {
        toggleDrawer();
        disableClosingDrawer(false);
        openModal({ type: "REMOTE_LAUNCH_SUCCESS", eventType: 'DISABLE_BACKDROP', cancelButtonLabel: "Close" });
    };

    function getRecommendedScreen(studentAge: number) {
        // NOTE: FOR NOW WE COMMENT THIS OUT BECAUSE WE ONLY HAVE TWEENSCREEN HERE
        if (studentAge < 10) {
            return "tScreen"
        } else if (studentAge >= 10 && studentAge < 13) {
            return "TweenScreen"
        }
        // else if (studentAge >= 13) {
        //   return "TeenScreen"
        // } 
        else {
            return "TweenScreen"
        }
    }
    function SelectScreenWrapper({ onSelect, subscriptions }: SelectScreenProps) {
        let dateISO = parseISO(dateOnlyISO8601(studentData!.dob.toString()))
        const studentAge = differenceInYears(new Date(), dateISO)
        const recommendedScreen = getRecommendedScreen(studentAge)
        return <Grid container spacing={2} direction="column"
            alignItems="center"
            sx={{ padding: "36px 16px", textAlign: "center" }}>
            <Grid size={12} direction="column" alignItems="center">
                <Typography>Based on the child's age ({studentAge}) we recommend they take the <strong>{recommendedScreen}</strong></Typography>
            </Grid>
            <SelectScreenVersion onSelect={onSelect} subscriptions={subscriptions} />
        </Grid>
    }
    if (!screenType) {
        return <SelectScreenWrapper subscriptions={subscriptions} onSelect={handleSelectScreen} />
    }
    const handleBack = () => {
        setScreenType(null)
    }
    return (
        <Grid
            container
            spacing={8}
            direction="column"
            alignItems="center"
            sx={{ padding: "36px 16px", textAlign: "center" }}
        >
            <IconButton sx={{ position: "absolute", top: "10px", left: "10px", zIndex: "9999", color: "#fff" }}
                onClick={handleBack}
            ><ArrowBack /></IconButton>
            <Grid size={12} direction="column" alignItems="center">
                <Typography>
                    Launch the child's assessment in your browser window immediately.
                </Typography>
                <LaunchButton
                    action="local"
                    currentAction={launchAction}
                    onAction={handleLaunch}
                >
                    Local Launch
                </LaunchButton>
            </Grid>
            <Grid size={12} direction="column" alignItems="center">
                <Typography>
                    Send a secure email so the child can take the
                    assessment remotely.
                </Typography>
                <Grid size={12} direction='column' alignItems='center'>
                    <Grid container direction='column' alignItems="center">
                        <SendToWhichEmail studentData={studentData as Student} updateStudent={updateStudent} onSelect={handleEmailSelect} loadStatus={remoteLoadStatus} />
                    </Grid>
                </Grid>
            </Grid>
            <Grid size={12} direction="column" alignItems="center">
                <Typography>
                    Prepare a link that I will send myself.
                </Typography>
                <LaunchButton
                    action="link"
                    currentAction={launchAction}
                    onAction={handleLaunch}
                >
                    Generate Link
                </LaunchButton>
            </Grid>
        </Grid>
    );
}
